import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { AppRoutes } from 'constants/routes';

import { getTimeAgoValue } from 'helpers/date';

import useOnClickOutside from 'hooks/useOutsideClick';

import useAuthStore from 'store/authStore/useAuthStore';

import { ModalService, NotificationsService } from 'services/globalServices';
import { NotificationsEvent } from 'services/notifications.service';

import { NotificationFeedItem, NotificationFeedItemField } from 'types/notifications';

import IconBell from 'assets/icons/nav/icon-bell-16.svg';
import IconSettings from 'assets/icons/nav/icon-settings-16.svg';

import { DotsLoader } from 'basics/loaders';

import ChooseLoginMethodModal from 'modals/auth/ChooseLoginMethodModal';

import {
    BellButton,
    BellWrapper,
    Dropdown,
    DropdownHeader,
    DropdownTitle,
    EmptyState,
    FeedItemField,
    FeedItemFieldLabel,
    FeedItemFields,
    FeedItemFieldValue,
    FeedItemHeader,
    FeedItemLink,
    FeedItemMeta,
    FeedItemSummary,
    FeedItemTime,
    FeedItemTitle,
    FeedItemWrapper,
    FeedList,
    HeaderActions,
    MarkReadButton,
    SettingsLink,
    UnreadBadge,
    UnreadDot,
} from './NotificationsBlock.styled';

const FEED_LIMIT = 20;
const FEED_POLL_INTERVAL_MS = 10 * 1000;

// Rich field value: segments let a part of the value (e.g. the previous
// amount in "old → new") render struck through.
const FieldValueContent = ({ field }: { field: NotificationFeedItemField }) =>
    field.segments?.length ? (
        <>
            {field.segments.map((segment, index) => {
                const text = segment.strikethrough ? <s>{segment.text}</s> : segment.text;
                // Segment-level link: e.g. the market name in a rewards row.
                return segment.href ? (
                    <a
                        key={index}
                        href={segment.href}
                        target="_blank"
                        rel="noreferrer"
                        onClick={event => event.stopPropagation()}
                    >
                        {text}
                    </a>
                ) : (
                    <React.Fragment key={index}>{text}</React.Fragment>
                );
            })}
        </>
    ) : (
        <>{field.value}</>
    );

const NotificationsBlock = () => {
    const { account, isLogged } = useAuthStore();
    const navigate = useNavigate();

    const [hasSession, setHasSession] = useState(false);
    const [unread, setUnread] = useState(0);
    const [isFeedEnabled, setIsFeedEnabled] = useState(true);
    const [items, setItems] = useState<NotificationFeedItem[] | null>(null);
    // Local-only test item: the backend deliberately persists nothing for
    // tests, so the bell renders its copy client-side until read/cleared.
    const [testItem, setTestItem] = useState<NotificationFeedItem | null>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    // Items that were unread when the dropdown was opened: reading happens
    // automatically on open (single watermark on the backend), but the dots
    // stay visible until the dropdown closes so "what's new" is still clear.
    const [sessionUnreadIds, setSessionUnreadIds] = useState<Set<number>>(new Set());
    // Badge count frozen at the moment the dropdown opens: reading happens
    // immediately, but the number only disappears when the dropdown closes.
    const [frozenUnread, setFrozenUnread] = useState(0);

    const closeDropdown = useCallback(() => {
        setIsOpen(false);
        setSessionUnreadIds(new Set());
        setFrozenUnread(0);
    }, []);

    const menuRef = useRef(null);
    useOnClickOutside(menuRef, closeDropdown);

    const loadFeed = useCallback(() => {
        setIsLoading(true);
        NotificationsService.getFeed(FEED_LIMIT)
            .then(feed => {
                setItems(feed.items);
                setUnread(feed.unread);
                setIsFeedEnabled(feed.enabled);
            })
            .catch(() => setItems([]))
            .finally(() => setIsLoading(false));
    }, []);

    // Resume the notifications session silently (never prompts the wallet).
    useEffect(() => {
        if (!isLogged || !account) {
            setHasSession(false);
            setItems(null);
            setUnread(0);
            NotificationsService.resetInMemorySession();
            return;
        }

        NotificationsService.tryResumeSession(account.accountId()).then(resumed => {
            setHasSession(resumed);
            if (resumed) {
                loadFeed();
            }
        });
    }, [isLogged, account, loadFeed]);

    // Background refresh: keep the unread badge current while the app is open.
    // Skipped when the tab is hidden; the next visible tick catches up.
    useEffect(() => {
        if (!hasSession) {
            return;
        }
        const interval = setInterval(() => {
            if (!document.hidden) {
                loadFeed();
            }
        }, FEED_POLL_INTERVAL_MS);
        return () => clearInterval(interval);
    }, [hasSession, loadFeed]);

    useEffect(
        () =>
            NotificationsService.event.sub(event => {
                if (event.type === NotificationsEvent.unreadChanged) {
                    setUnread(event.unread ?? 0);
                }
                if (event.type === NotificationsEvent.sessionChanged) {
                    setHasSession(Boolean(event.isAuthenticated));
                }
                if (event.type === NotificationsEvent.feedEnabledChanged) {
                    setIsFeedEnabled(Boolean(event.enabled));
                }
                if (event.type === NotificationsEvent.testNotificationSent) {
                    setTestItem({
                        id: 0,
                        event_type: 'test_notification',
                        title: 'Test notification',
                        summary:
                            'Your Aquarius notification settings are working. ' +
                            'This channel is connected and ready to receive updates.',
                        primary_link: null,
                        created_at: new Date().toISOString(),
                        unread: true,
                    });
                }
            }),
        [],
    );

    // The bell disappears entirely while in-app notifications are disabled;
    // they can be re-enabled from the notification settings page.
    if (isLogged && hasSession && !isFeedEnabled) {
        return null;
    }

    const toggleOpen = () => {
        if (!isLogged) {
            closeDropdown();
            ModalService.openModal(ChooseLoginMethodModal, {
                redirectURL: AppRoutes.page.notifications,
            });
            return;
        }

        const next = !isOpen;

        if (next) {
            setIsOpen(true);
            // Opening reads everything: capture the unread ids for the session
            // dots and freeze the badge count, then advance the read watermark.
            setFrozenUnread(unread + (testItem?.unread ? 1 : 0));
            if (testItem?.unread) {
                setSessionUnreadIds(current => new Set([...current, testItem.id]));
                setTestItem(current => (current ? { ...current, unread: false } : current));
            }
            if (hasSession) {
                setIsLoading(true);
                NotificationsService.getFeed(FEED_LIMIT)
                    .then(feed => {
                        setItems(feed.items);
                        setIsFeedEnabled(feed.enabled);
                        setSessionUnreadIds(current => {
                            const ids = new Set(current);
                            feed.items
                                .filter(item => item.unread)
                                .forEach(item => ids.add(item.id));
                            return ids;
                        });
                        setUnread(0);
                        if (feed.unread > 0) {
                            return NotificationsService.markFeedRead().catch(() => {
                                // Non-critical: the badge returns on the next poll.
                            });
                        }
                    })
                    .catch(() => setItems([]))
                    .finally(() => setIsLoading(false));
            }
            return;
        }

        closeDropdown();
    };

    const onClearAll = () => {
        setTestItem(null);
        setSessionUnreadIds(new Set());
        setFrozenUnread(0);
        NotificationsService.clearFeed()
            .then(() => {
                setItems([]);
                setUnread(0);
            })
            .catch(() => {
                // Non-critical: the list simply stays until the next load.
            });
    };

    const visibleItems = testItem ? [testItem, ...(items ?? [])] : items;
    const totalUnread = unread + (testItem?.unread ? 1 : 0);
    // While the dropdown is open the count stays frozen so it does not vanish
    // mid-reading; it disappears when the dropdown closes.
    const badgeCount = isOpen ? frozenUnread : totalUnread;

    const goToSettings = () => {
        closeDropdown();
        navigate(AppRoutes.page.notifications);
    };

    // Backend links point at aqua.network pages: follow them via the router to
    // keep SPA navigation (and to work on dev/staging hosts); anything else
    // opens in a new tab.
    const onItemClick = (item: NotificationFeedItem) => {
        if (!item.primary_link) {
            return;
        }
        try {
            const url = new URL(item.primary_link.url);
            if (url.hostname === 'aqua.network' || url.hostname === 'testnet.aqua.network') {
                closeDropdown();
                navigate(`${url.pathname}${url.search}`);
                return;
            }
        } catch {
            return;
        }
        window.open(item.primary_link.url, '_blank', 'noopener');
    };

    return (
        <BellWrapper ref={menuRef}>
            <BellButton onClick={toggleOpen} aria-label="Notifications">
                <IconBell />
                {isLogged && Boolean(badgeCount) && (
                    <UnreadBadge>{badgeCount > 99 ? '99+' : badgeCount}</UnreadBadge>
                )}
            </BellButton>

            {isLogged && isOpen && (
                <Dropdown>
                    <DropdownHeader>
                        <DropdownTitle>Notifications</DropdownTitle>
                        <HeaderActions>
                            {hasSession && isFeedEnabled && Boolean(visibleItems?.length) && (
                                <MarkReadButton onClick={onClearAll}>Clear all</MarkReadButton>
                            )}
                        </HeaderActions>
                    </DropdownHeader>

                    {!hasSession && (
                        <EmptyState>
                            Enable notifications to see Aquarius updates here
                            <MarkReadButton onClick={goToSettings}>
                                Enable notifications
                            </MarkReadButton>
                        </EmptyState>
                    )}

                    {hasSession && isLoading && items === null && (
                        <EmptyState>
                            <DotsLoader />
                        </EmptyState>
                    )}

                    {hasSession && visibleItems !== null && !visibleItems.length && (
                        <EmptyState>No notifications yet</EmptyState>
                    )}

                    {hasSession && Boolean(visibleItems?.length) && (
                        <FeedList>
                            {visibleItems.map(item => {
                                const isNew = item.unread || sessionUnreadIds.has(item.id);
                                return (
                                    <FeedItemWrapper key={item.id}>
                                        <FeedItemHeader>
                                            <FeedItemTitle $unread={isNew}>
                                                {item.title}
                                            </FeedItemTitle>
                                            <FeedItemMeta>
                                                <FeedItemTime>
                                                    {getTimeAgoValue(item.created_at)}
                                                </FeedItemTime>
                                                {isNew && <UnreadDot />}
                                            </FeedItemMeta>
                                        </FeedItemHeader>
                                        <FeedItemSummary>
                                            {item.summary_parts?.length
                                                ? item.summary_parts.map((part, index) =>
                                                      part.href ? (
                                                          <a
                                                              key={index}
                                                              href={part.href}
                                                              target="_blank"
                                                              rel="noreferrer"
                                                              onClick={event =>
                                                                  event.stopPropagation()
                                                              }
                                                          >
                                                              {part.text}
                                                          </a>
                                                      ) : (
                                                          <React.Fragment key={index}>
                                                              {part.text}
                                                          </React.Fragment>
                                                      ),
                                                  )
                                                : item.summary}
                                        </FeedItemSummary>
                                        {Boolean(item.fields?.length) && (
                                            <FeedItemFields>
                                                {/* Labels repeat (several "Removed" rows), so index the key. */}
                                                {item.fields.map((field, index) => (
                                                    <FeedItemField key={`${field.label}-${index}`}>
                                                        <FeedItemFieldLabel>
                                                            {field.label}:
                                                        </FeedItemFieldLabel>{' '}
                                                        <FeedItemFieldValue
                                                            $strikethrough={field.strikethrough}
                                                        >
                                                            {field.href ? (
                                                                <a
                                                                    href={field.href}
                                                                    target="_blank"
                                                                    rel="noreferrer"
                                                                    onClick={event =>
                                                                        event.stopPropagation()
                                                                    }
                                                                >
                                                                    <FieldValueContent
                                                                        field={field}
                                                                    />
                                                                </a>
                                                            ) : (
                                                                <FieldValueContent field={field} />
                                                            )}
                                                        </FeedItemFieldValue>
                                                    </FeedItemField>
                                                ))}
                                            </FeedItemFields>
                                        )}
                                        {item.primary_link && (
                                            <FeedItemLink onClick={() => onItemClick(item)}>
                                                {item.primary_link.label}
                                            </FeedItemLink>
                                        )}
                                    </FeedItemWrapper>
                                );
                            })}
                        </FeedList>
                    )}

                    <SettingsLink onClick={goToSettings}>
                        <IconSettings />
                        Notification settings
                    </SettingsLink>
                </Dropdown>
            )}
        </BellWrapper>
    );
};

export default NotificationsBlock;
